import React, { useState } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./styles/App.css";
import logo from "./assets/logo_d/logo_d_without_background.png";
import Home from "./components/Home";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import Eula from "./components/Eula";
import NotFound from "./components/NotFound";
import LanguageSwitcher from "./components/LanguageSwitcher";

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleNavigationClick = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <Link
            to="/"
            className="logo-link"
            onClick={(e) => handleNavigationClick(e, "home")}
          >
            <img src={logo} alt="Dialecto" className="logo" />
          </Link>
          <nav className="desktop-nav">
            <ul className="nav-links">
              <li>
                <Link to="/" onClick={(e) => handleNavigationClick(e, "home")}>
                  {t("header.home")}
                </Link>
              </li>
              <li>
                <a
                  href="#features"
                  onClick={(e) => handleNavigationClick(e, "features")}
                >
                  {t("header.features")}
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  onClick={(e) => handleNavigationClick(e, "pricing")}
                >
                  {t("header.pricing")}
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={(e) => handleNavigationClick(e, "about")}
                >
                  {t("header.about")}
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => handleNavigationClick(e, "contact")}
                >
                  {t("header.contact")}
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <div className="header-right">
          <LanguageSwitcher />
          <a
            href="https://apps.apple.com/pl/app/dialecto/id6746904195"
            target="_blank"
            rel="noopener noreferrer"
            className="header-cta"
          >
            {t("header.downloadApp")}
          </a>
          <div className="burger" onClick={toggleMenu}>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <nav className={`mobile-nav ${menuOpen ? "active" : ""}`}>
            <LanguageSwitcher />
            <ul className="nav-links">
              <li>
                <Link to="/" onClick={(e) => handleNavigationClick(e, "home")}>
                  {t("header.home")}
                </Link>
              </li>
              <li>
                <a
                  href="#features"
                  onClick={(e) => handleNavigationClick(e, "features")}
                >
                  {t("header.features")}
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  onClick={(e) => handleNavigationClick(e, "pricing")}
                >
                  {t("header.pricing")}
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  onClick={(e) => handleNavigationClick(e, "about")}
                >
                  {t("header.about")}
                </a>
              </li>
              <li>
                <a
                  href="#contact"
                  onClick={(e) => handleNavigationClick(e, "contact")}
                >
                  {t("header.contact")}
                </a>
              </li>
            </ul>
            <a
              href="https://apps.apple.com/pl/app/dialecto/id6746904195"
              target="_blank"
              rel="noopener noreferrer"
              className="mobile-cta"
              onClick={() => setMenuOpen(false)}
            >
              {t("header.downloadApp")}
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/eula" element={<Eula />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <p className="footer-brand-name">{t("footer.brandName")}</p>
          <p className="footer-tagline-text">{t("footer.tagline")}</p>
          
          {/* Social Media Links */}
          <div className="footer-social">
            <p className="footer-social-label">{t("socialMedia.followUs")}</p>
            <div className="footer-social-links">
              <a
                href="https://www.instagram.com/dialectoapp/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link instagram"
                aria-label="Follow Dialecto on Instagram"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@dialecto74"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link tiktok"
                aria-label="Follow Dialecto on TikTok"
              >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.1a8.16 8.16 0 0 0 4.76 1.52v-3.4c-.72 0-1.41-.16-2.03-.44l.03.01z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-legal">
            <Link to="/privacy">{t("footer.privacyPolicy")}</Link>
            <span className="footer-divider">•</span>
            <Link to="/terms">{t("footer.termsAndConditions")}</Link>
            <span className="footer-divider">•</span>
            <Link to="/eula">{t("footer.eula")}</Link>
          </div>
          <p className="footer-copyright">{t("footer.copyright")}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
